import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PortfolioReport, PositionSummary } from '@covered-calls/shared';
import { ConnectionStatus, PositionStatus } from '@covered-calls/shared';
import { apiClient, ApiClientError } from '../services/api-client';
import { useIbkrLiveStatus } from '../hooks/useIbkrLiveStatus';
import type { LiveStatusUiState } from '../hooks/useIbkrLiveStatus';

/**
 * Dashboard — Portfolio Performance overview with clickable positions.
 */
export function Dashboard() {
  const navigate = useNavigate();
  const [report, setReport] = useState<PortfolioReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<PortfolioReport>('/reports/portfolio');
      setReport(data);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to load portfolio performance.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (isLoading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-muted)' }}>Loading portfolio...</div>;
  }

  if (error) {
    return (
      <div role="alert" style={{ padding: '1rem', backgroundColor: 'var(--color-chip-danger-bg)', color: 'var(--color-chip-danger-text)', borderRadius: '4px' }}>
        <p>{error}</p>
        <button onClick={fetchReport} style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)', border: 'none', borderRadius: '4px', cursor: 'pointer', minHeight: '44px' }}>
          Retry
        </button>
      </div>
    );
  }

  if (!report) return null;

  // Dashboard lists current holdings only. Closed positions still appear in
  // the Reports view; the summary cards above continue to reflect all positions.
  const openPositions = report.positions.filter((pos) => pos.status === PositionStatus.OPEN);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Portfolio Performance</h2>

      {/* IBKR Connection Status */}
      <IbkrStatusIndicator />

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <SummaryCard label="Total Premium Income" value={formatCurrency(report.totalPremiumIncome)} />
        <SummaryCard label="Realised Gains" value={formatCurrency(report.totalRealisedGains)} color="var(--color-pnl-positive)" />
        <SummaryCard label="Realised Losses" value={formatCurrency(report.totalRealisedLosses)} color="var(--color-pnl-negative)" />
        <SummaryCard
          label="Realised Return %"
          value={formatPercent(report.realisedReturnPercentage)}
          color={report.realisedReturnPercentage >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)'}
        />
        <SummaryCard label="Unrealised Gains" value={formatCurrency(report.totalUnrealisedGains)} color="var(--color-pnl-positive)" />
        <SummaryCard label="Unrealised Losses" value={formatCurrency(report.totalUnrealisedLosses)} color="var(--color-pnl-negative)" />
        <SummaryCard
          label="Unrealised Return %"
          value={formatPercent(report.unrealisedReturnPercentage)}
          color={report.unrealisedReturnPercentage >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)'}
        />
        <SummaryCard label="Total Commissions" value={formatCurrency(report.totalCommissionsPaid)} />
        <SummaryCard label="Dividend Income" value={formatCurrency(report.totalDividendIncome)} color="var(--color-pnl-positive)" />
        <SummaryCard label="Protective Put Cost" value={formatCurrency(report.totalProtectivePutCost)} color="var(--color-pnl-negative)" />
        <SummaryCard
          label="Net Return"
          value={formatCurrency(report.netPortfolioReturn)}
          color={report.netPortfolioReturn >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)'}
        />
        <SummaryCard label="Capital Invested" value={formatCurrency(report.totalCapitalInvested)} />
      </div>

      {/* Positions table — dashboard shows OPEN (current) positions only.
          Closed positions remain visible in the Reports view. */}
      <h2 style={{ marginBottom: '0.75rem' }}>Open Positions</h2>
      {openPositions.length === 0 ? (
        <p style={{ color: 'var(--color-muted)' }}>No open positions found.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }} aria-label="Open positions">
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 0.5rem' }}>Ticker</th>
                <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Premium</th>
                <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Stock Gain/Loss</th>
                <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Commissions</th>
                <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Net P&L</th>
              </tr>
            </thead>
            <tbody>
              {openPositions.map((pos) => (
                <tr
                  key={pos.positionId}
                  onClick={() => navigate(`/trades/${pos.positionId}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/trades/${pos.positionId}`); } }}
                  tabIndex={0}
                  role="row"
                  aria-label={`${pos.ticker} position`}
                  style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer', transition: 'background-color 0.15s' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-bg-alt)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                >
                  <td style={{ padding: '0.75rem 0.5rem', fontWeight: 600 }}>{pos.ticker}</td>
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(pos.premiumReceived)}</td>
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', color: pos.stockGainLoss >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)' }}>{formatCurrency(pos.stockGainLoss)}</td>
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatCurrency(pos.commissionsPaid)}</td>
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 600, color: pos.netProfitLoss >= 0 ? 'var(--color-pnl-positive)' : 'var(--color-pnl-negative)' }}>{formatCurrency(pos.netProfitLoss)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '8px', backgroundColor: 'var(--color-surface)' }}>
      <div style={{ fontSize: '0.7rem', color: 'var(--color-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: color || 'var(--color-text)' }}>{value}</div>
    </div>
  );
}

function formatCurrency(value: number): string {
  const prefix = value < 0 ? '-$' : '$';
  return `${prefix}${Math.abs(value).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

const IBKR_STATUS_COLORS: Record<LiveStatusUiState, string> = {
  [ConnectionStatus.CONNECTED]: 'var(--color-success)',
  [ConnectionStatus.DISCONNECTED]: 'var(--color-muted)',
  [ConnectionStatus.AUTH_FAILED]: 'var(--color-danger)',
  CHECKING: 'var(--color-muted)',
  UNKNOWN: 'var(--color-muted)',
};

const IBKR_STATUS_LABELS: Record<LiveStatusUiState, string> = {
  [ConnectionStatus.CONNECTED]: 'IBKR Connected',
  [ConnectionStatus.DISCONNECTED]: 'IBKR Disconnected',
  [ConnectionStatus.AUTH_FAILED]: 'IBKR Auth Failed',
  CHECKING: 'Checking…',
  UNKNOWN: 'IBKR Status Unknown',
};

/** Formats an ISO-8601 timestamp into a locale time string for the hint. */
function formatLastChecked(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleTimeString();
}

export function IbkrStatusIndicator() {
  const { status, checkedAt, isChecking, message, refresh } = useIbkrLiveStatus();
  const color = IBKR_STATUS_COLORS[status];
  const label = IBKR_STATUS_LABELS[status];
  const isAuthFailed = status === ConnectionStatus.AUTH_FAILED;
  // Show the backend's explanatory message when the connection is not healthy.
  const showMessage =
    !!message && status !== ConnectionStatus.CONNECTED && status !== 'CHECKING';

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.35rem',
        marginBottom: '1rem',
        padding: isAuthFailed ? '0.5rem 0.75rem' : '0.375rem 0.75rem',
        backgroundColor: isAuthFailed ? 'var(--color-chip-danger-bg)' : 'var(--color-surface-2)',
        border: `1px solid ${isAuthFailed ? 'var(--color-danger)' : 'var(--color-border)'}`,
        borderRadius: '6px',
        fontSize: '0.8rem',
        width: 'fit-content',
        maxWidth: '520px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <span style={{ color: isAuthFailed ? 'var(--color-chip-danger-text)' : 'var(--color-text)', fontWeight: 500 }}>
        {label}
      </span>
      {checkedAt && (
        <span style={{ color: 'var(--color-muted)', fontSize: '0.75rem' }}>
          · last checked {formatLastChecked(checkedAt)}
        </span>
      )}
      <button
        type="button"
        onClick={refresh}
        disabled={isChecking}
        aria-label="Refresh IBKR connection status"
        style={{
          marginLeft: '0.25rem',
          padding: '0.125rem 0.5rem',
          fontSize: '0.75rem',
          border: '1px solid var(--color-input-border)',
          borderRadius: '4px',
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text)',
          cursor: isChecking ? 'not-allowed' : 'pointer',
        }}
      >
        {isChecking ? 'Checking…' : 'Refresh'}
      </button>
      {isAuthFailed && (
        <a
          href="/settings"
          style={{
            color: 'var(--color-link)',
            fontSize: '0.75rem',
            textDecoration: 'underline',
            marginLeft: '0.25rem',
          }}
        >
          Fix in Settings
        </a>
      )}
      </div>
      {showMessage && (
        <span style={{ color: isAuthFailed ? 'var(--color-chip-danger-text)' : 'var(--color-muted)', fontSize: '0.75rem', lineHeight: 1.35 }}>
          {message}
        </span>
      )}
    </div>
  );
}
